import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, ExternalLink, Loader2, AlertTriangle } from "lucide-react";

/**
 * ConnectStatusBanner — Stripe Connect Express activation banner.
 * Shown ONLY once the candidate profile has been validated by the admin.
 * States:
 *   - Not connected      → CTA "Activer mes paiements"
 *   - KYC in progress    → CTA "Compléter l'onboarding" (currently_due list)
 *   - Payouts enabled    → green pill "Compte Stripe actif"
 */
export default function ConnectStatusBanner({ profileStatus }) {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const validated = ["validated", "premium"].includes(profileStatus);

    const refresh = async () => {
        if (!validated) return;
        try {
            const r = await api.get("/candidates/me/connect/status");
            setStatus(r.data);
        } catch (_) {}
        finally { setLoading(false); }
    };
    useEffect(() => {
        refresh();
        // Auto-refresh once when arriving from Stripe with ?connect=success
        try {
            const url = new URL(window.location.href);
            if (url.searchParams.get("connect") === "success") {
                toast.success("Merci ! Nous vérifions votre onboarding Stripe…");
                url.searchParams.delete("connect");
                window.history.replaceState({}, "", url.toString());
                setTimeout(refresh, 1500);
            }
        } catch (_) {}
    }, [validated]);

    if (!validated) return null;
    if (loading) return null;
    if (!status) return null;

    const activate = async () => {
        setBusy(true);
        try {
            const r = await api.post("/candidates/me/connect/onboarding");
            if (r.data?.url) {
                window.location.assign(r.data.url);
            } else {
                toast.error("Lien Stripe indisponible — réessayez dans un instant");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Impossible de créer le lien Stripe");
        } finally {
            setBusy(false);
        }
    };

    const active = status.details_submitted && status.payouts_enabled;
    if (active) {
        return (
            <div
                className="mb-6 rounded-xl border-l-4 border-emerald-500 bg-emerald-50 p-4 flex items-start gap-3"
                data-testid="connect-active-banner"
            >
                <div className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                    <ShieldCheck size={18} />
                </div>
                <div className="text-sm text-slate-700 leading-relaxed flex-1">
                    <div className="font-semibold text-emerald-900 mb-0.5">
                        Compte de paiement Stripe actif
                    </div>
                    Vos rémunérations sont automatiquement transférées sur votre compte
                    bancaire (via Stripe Express) dès validation du timesheet par le client.
                    <b> Aucune démarche à faire.</b>
                </div>
            </div>
        );
    }

    const inProgress = !!status.stripe_account_id;
    return (
        <div
            className="mb-6 rounded-xl border-l-4 border-[#F5A623] bg-amber-50 p-4 flex items-start gap-3"
            data-testid="connect-inactive-banner"
        >
            <div className="w-9 h-9 rounded-full bg-[#F5A623] text-[#0B192C] flex items-center justify-center shrink-0">
                {inProgress ? <AlertTriangle size={18} /> : <CreditCard size={18} />}
            </div>
            <div className="text-sm text-slate-700 leading-relaxed flex-1">
                <div className="font-semibold text-[#0B192C] mb-1">
                    {inProgress
                        ? "Onboarding Stripe à finaliser"
                        : "Activez vos paiements automatiques (Stripe Connect)"}
                </div>
                {inProgress ? (
                    <>
                        Il vous reste quelques informations à renseigner chez Stripe pour
                        activer les virements automatiques.
                        {status.currently_due?.length > 0 && (
                            <div className="mt-1 text-xs text-slate-600">
                                Informations demandées : {status.currently_due.join(", ")}
                            </div>
                        )}
                        {status.disabled_reason && (
                            <div className="mt-1 text-xs text-red-700">
                                Motif Stripe : {status.disabled_reason}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        Renseignez vos coordonnées bancaires directement chez Stripe (KYC
                        sécurisé, ~3 min). Après chaque validation de timesheet client,
                        vos versements arrivent automatiquement sur votre compte,
                        avec les frais Renforteo (8 %) déduits.
                    </>
                )}
                <div className="mt-3">
                    <button
                        onClick={activate}
                        disabled={busy}
                        className="btn-gold text-sm disabled:opacity-50 inline-flex items-center gap-2"
                        data-testid="connect-activate-btn"
                    >
                        {busy ? (<><Loader2 size={14} className="animate-spin" />Ouverture Stripe…</>) : (
                            <>
                                {inProgress ? "Reprendre l'onboarding" : "Activer mes paiements"}
                                <ExternalLink size={14} />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
